// REST of the device functions (APP-PARITY.md "Detail-Abgleich"): the log and the crash report, the recovery of the last
// known settings (instead of backups, Dominik 19.10.2026), the export (app-v1 http-diag, http-recovery, http-export).
import type { DiagResponse, RecoveryResponse, RecoveryResult } from '@brewcore/app-contract';
import { jsonRequest, type FetchLike, type HttpResult, type HttpTarget } from './http.js';
import type { Lang } from './i18n.js';

export class DeviceApi {
  private readonly target: HttpTarget;

  constructor(
    fetchImpl: FetchLike = (input, init) => fetch(input, init),
    base = '',
    headers: Record<string, string> = {},
  ) {
    this.target = { fetchImpl, base, headers };
  }

  /** diag.log / diag.crash from the machine now (blocks until it answered). */
  diag(machine: string, kind: 'log' | 'crash'): Promise<HttpResult<DiagResponse>> {
    return jsonRequest(this.target, 'POST', `/api/machines/${machine}/diag`, { kind }, 'http-diag');
  }

  /** The last known settings offered after a data loss or a continued coupling (404 not_found: nothing offered). */
  recovery(machine: string): Promise<HttpResult<RecoveryResponse>> {
    return jsonRequest(this.target, 'GET', `/api/machines/${machine}/recovery`, null, 'http-recovery');
  }

  /** Takes the chosen values over (owner, fresh login, the S values among them confirmed). */
  recover(machine: string, params: string[], confirmed: string[]): Promise<HttpResult<RecoveryResult>> {
    return jsonRequest(
      this.target,
      'POST',
      `/api/machines/${machine}/recovery`,
      { params, confirmed },
      'http-recovery-result',
    );
  }

  /** "Nein, so lassen": the machine keeps its settings (owner). */
  dismissRecovery(machine: string): Promise<HttpResult<null>> {
    return jsonRequest(this.target, 'DELETE', `/api/machines/${machine}/recovery`, null, null);
  }

  /** Where the browser downloads the export (the session cookie goes along, same origin). */
  exportHref(machine: string): string {
    return `${this.target.base}/api/machines/${machine}/export`;
  }

  csvHref(machine: string, lang: Lang): string {
    return `${this.target.base}/api/machines/${machine}/export/shots.csv?lang=${lang}`;
  }
}
